const _ = require('lodash');

// Tag merchant destinations from Offers without changing discount or affiliate identifiers.
const addOfferUtmParams = (rawUrl, offerTitle) => {
  const url = parseOfferShoppingUrl(rawUrl);
  if (!url) return null;
  url.searchParams.set('utm_source', 'ShopMy');
  url.searchParams.set('utm_medium', 'private_offers');
  const campaign = _.snakeCase(offerTitle).replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  url.searchParams.delete('utm_campaign');
  if (campaign) url.searchParams.set('utm_campaign', campaign);
  return url.href;
};

// Only the displayed offer's usable, server-authorized code may decorate its shopping links.
const getOfferShoppingUrl = ({ Offer_id, code, url, fallbackUrl }, now = new Date()) => {
  const authorizedUrl =
    isOfferCodeUsable(code, now, { checkStartsAt: false }) && Number(Offer_id) > 0 && Number(code.Offer_id) === Number(Offer_id)
      ? parseOfferShoppingUrl(code.shopUrl, { httpsOnly: true })
      : null;
  const hasAuthorizedCode =
    authorizedUrl?.searchParams.getAll('smscode').length === 1 && authorizedUrl.searchParams.get('smscode') === code.displayText;
  const destination = parseOfferShoppingUrl(url === undefined ? (hasAuthorizedCode ? authorizedUrl.href : fallbackUrl) : url);
  if (!destination) return null;
  destination.searchParams.delete('smscode');
  if (hasAuthorizedCode && destination.origin === authorizedUrl.origin) destination.searchParams.set('smscode', code.displayText);
  return destination.href;
};

// Clients trust the server-authorized shopUrl's start time to tolerate device clock skew.
const isOfferCodeUsable = (code, now = new Date(), { checkStartsAt = true } = {}) => {
  if (code?.issuanceStatus !== 'active' || code.isBlacklisted || code.lastRedeemedAt) return false;
  if (typeof code.displayText !== 'string' || !/^[a-zA-Z0-9_!.-]+$/.test(code.displayText)) return false;
  const timestamp = new Date(now).getTime();
  if (!Number.isFinite(timestamp)) return false;
  return [
    ...(checkStartsAt ? [['startsAt', time => time <= timestamp]] : []),
    ['endsAt', time => time > timestamp]
  ].every(([field, isValid]) => {
    const value = code[field];
    if (value == null) return true;
    if (typeof value !== 'string' && !(value instanceof Date)) return false;
    const time = new Date(value).getTime();
    return Number.isFinite(time) && isValid(time);
  });
};

// Reject credentials and non-web schemes for both ordinary and code-bearing destinations.
const parseOfferShoppingUrl = (raw, { httpsOnly = false, originOnly = false } = {}) => {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const value = raw.trim();
  if (value.startsWith('/') || value.includes('\\') || [...value].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || (httpsOnly && url.protocol !== 'https:')) return null;
    if (originOnly && (url.pathname !== '/' || url.search || url.hash)) return null;
    return url;
  } catch {
    return null;
  }
};

const getShopperOffersAppLink = (Offer_id, origin = 'https://shopmy.us') => {
  const offerId = Number(Offer_id) || null;
  const webFallback = `${origin}/offers${offerId ? `?offer=${offerId}` : ''}`;
  // deferred-prompt + deepLinkData rides the app's AppsFlyer prompt pipeline
  // (appsflyer_helpers.js in the app repo), which auto-executes for logged-in users.
  const deepLinkData = { tab: 'me', meSection: 'offers', ...(offerId ? { Offer_id: offerId } : {}) };
  const urlParams = new URLSearchParams();
  urlParams.set('af_android_url', webFallback);
  urlParams.set('af_ios_url', webFallback);
  urlParams.set('af_web_dp', webFallback);
  urlParams.set('af_xp', 'custom');
  urlParams.set('pid', 'shopper_offers_web');
  urlParams.set('c', offerId ? 'me_offer_detail' : 'me_offers_hub');
  urlParams.set('deep_link_value', 'deferred-prompt');
  urlParams.set('deep_link_sub1', JSON.stringify({ title: 'View your Private Offers', deepLinkData }));
  return `https://shopmy.onelink.me/NXOi?${urlParams.toString()}`;
};

module.exports = { addOfferUtmParams, getOfferShoppingUrl, getShopperOffersAppLink, isOfferCodeUsable, parseOfferShoppingUrl };
